import {observer} from 'mobx-react';
import 'moment/locale/pt-br';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {mergeAll, tap} from 'rxjs/operators';
import {AdminContext} from '../../apiContexts';
import NavVertical from '../../components/navVertical';
import useSocket from '../../hooks/use-socket';
import useStore from '../../hooks/use-store';
import {logOut} from '../../services/authService';
import NavBar from '../../views/admin/NavBar';
import EventContainer from '../event-container';
import {AdminGlobalStyle, AdminStyle} from '../../views/admin/styles';
import ModalEnum from '../../views/modals/modalEnum';
import history from '../../routes/history';
import PropTypes from 'prop-types';
import useServices from '../../hooks/use-services';
import useDevices from '../../hooks/use-devices';
import usePayProvider from '../../hooks/use-pay-provider';

let positionsCache;

const AdminContainer = (props) => {
    const {
        children,
    } = props;

    const intl = useIntl();
    const {positionService, driverService} = useServices();
    const {store} = useStore();
    const {devices} = useDevices();
    const {auth} = store;
    const {syncExternalApi} = usePayProvider();
    const {positions: positionsSocket} = useSocket();

    useEffect(() => {
        driverService.list().then((drivers) => {
            store.drivers.setDrivers(drivers);
        });
        (async () => {
            try {
               await syncExternalApi();
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);

    useEffect(() => {
        if (devices.list.length) {
            (async () => {
                if (!positionsCache && !devices.updatedPositions) {
                    positionsCache = await positionService.list();
                    devices.updatePositions([...positionsCache]);
                }
                positionsSocket
                    .pipe(
                        tap((positions) => {
                            if (positions.length > 0) {
                                devices.updatePositions(positions);
                                positionsCache = positions;
                            }
                        }),
                        mergeAll(),
                    )
                    .subscribe((position) => {
                        devices.updatePosition(position);
                    });
            })();
        }
    }, [devices.list, devices.updatedPositions]);

    const links = useMemo(
        () => [
            {
                label: intl.formatMessage({id: 'mapTitle'}),
                icon: 'fas fa-map-marked-alt',
                type: ModalEnum.MAP,
                // TODO find a new way to avoid it, it was here to reload the map functions and sync again.
                 fn: () => history.push('/admin/devices'),
               //  fn: () => window.location.href = '/admin/devices',
            },
            {
                label: intl.formatMessage({id: 'settingsUser'}),
                icon: 'fas fa-user-tie',
                type: ModalEnum.ACCOUNT,
                fn: () => history.push('/admin/manage/account'),
            },
            {
                label: intl.formatMessage({id: 'settingsUsers'}),
                icon: 'fas fa-users',
                type: ModalEnum.USERS,
                fn: () => history.push('/admin/manage/users'),
            },
            {
                label: intl.formatMessage({id: 'groupDialog'}),
                icon: 'fas fa-object-group',
                type: ModalEnum.GROUP,
                fn: () => history.push('/admin/manage/groups'),
            },
            {
                label: intl.formatMessage({id: 'sharedGeofence'}),
                icon: 'fas fa-location-circle',
                type: ModalEnum.GEOFENCE,
                fn: () => history.push('/admin/manage/geofences'),
            },
            {
                label: intl.formatMessage({id: 'sharedDevice'}),
                icon: 'fas fa-car',
                type: ModalEnum.DEVICE,
                fn: () => history.push('/admin/manage/device'),
            },
            {
                label: intl.formatMessage({id: 'sharedDriver'}),
                icon: 'fas fa-steering-wheel',
                type: ModalEnum.DRIVER,
                fn: () => history.push('/admin/manage/drivers'),
            },
            {
                label: intl.formatMessage({id: 'sharedNotification'}),
                icon: 'fas fa-envelope-open-text',
                type: ModalEnum.NOTIFICATION,
                fn: () => history.push('/admin/manage/notifications'),
            },
            {
                label: intl.formatMessage({id: 'sharedCalendar'}),
                icon: 'fas fa-calendar-alt',
                type: ModalEnum.CALENDAR,
                fn: () => history.push('/admin/manage/calendars'),
            },
            {
                label: intl.formatMessage({id: 'sharedMaintenance'}),
                icon: 'fas fa-tools',
                type: ModalEnum.MAINTENANCE,
                fn: () => history.push('/admin/manage/maintenance'),
            },
            {
                label: intl.formatMessage({id: 'settingsServer'}),
                icon: 'fas fa-server',
                type: ModalEnum.SERVER,
                fn: () => history.push('/admin/manage/server'),
            },
            {
                label: intl.formatMessage({id: 'sharedLogout'}),
                icon: 'fas fa-door-open',
                fn: logOut,
            },
        ],
        [],
    );

    const filterLinks = useCallback(() => {
        const profile = auth.profile;
        if (profile === 'monitor') {
            return links.filter((l) => l.type !== ModalEnum.SERVER);
        }
        return links;
    }, [links]);

    return (
        <>
            <AdminGlobalStyle/>
            <AdminStyle>
                <AdminContext.Provider>
                    <NavBar devices={devices}/>
                    <EventContainer/>
                    <NavVertical links={filterLinks()}/>
                    <div id="children">{children}</div>
                </AdminContext.Provider>
            </AdminStyle>
        </>
    );
};

export default observer(AdminContainer);

AdminContainer.propTypes = {
    children: PropTypes.any,
};
